import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicSharesApi } from '../utils/api';
import { formatFileSize, downloadBlob, getFileTypeLabel, formatDate, is3DModel } from '../utils/helpers';
import ModelViewer from '../components/ModelViewer';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';
import * as XLSX from 'xlsx';
import {
  FileText, Table, Image, File, Download, Lock, Eye, EyeOff,
  Clock, User, Shield, ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
  AlertCircle, Calendar, Video, Box
} from 'lucide-react';

// Helper to format remaining time
const formatTimeRemaining = (expiresAt) => {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diff = expiry - now;

  if (diff <= 0) return 'Expired';

  const minutes = Math.floor(diff / (1000 * 60));
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days > 0) return `${days} day${days > 1 ? 's' : ''} remaining`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} remaining`;
  return `${minutes} minute${minutes > 1 ? 's' : ''} remaining`;
};

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PublicSharePage = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();

  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Password state
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [verifiedPassword, setVerifiedPassword] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Content state
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);

  // PDF state
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const canvasRef = useRef(null);

  // Excel state
  const [excelData, setExcelData] = useState(null);
  const [activeSheet, setActiveSheet] = useState(0);

  // Download state
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    loadShareInfo();
  }, [shareId]);

  useEffect(() => {
    if (pdfDoc && canvasRef.current) {
      renderPage(currentPage);
    }
  }, [pdfDoc, currentPage, scale]);

  useEffect(() => {
    if (verified && shareInfo) {
      loadContent();
    }
  }, [verified, shareInfo]);

  const loadShareInfo = async () => {
    try {
      setLoading(true);
      const response = await publicSharesApi.getInfo(shareId);
      const data = response.data;

      if (data.hasPassword) {
        setNeedsPassword(true);
        setShareInfo(data);
      } else {
        setShareInfo(data);
        setVerified(true);
      }
    } catch (err) {
      console.error('Load share info error:', err);
      if (err.response?.status === 404) {
        setNotFound(true);
      } else if (err.response?.status === 410) {
        setExpired(true);
      } else {
        setError('Failed to load shared file');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;

    try {
      setVerifying(true);
      setPasswordError(null);

      const response = await publicSharesApi.verifyPassword(shareId, password);

      if (response.data.valid) {
        setVerifiedPassword(password);
        setVerified(true);
        setNeedsPassword(false);
      }
    } catch (err) {
      console.error('Password verify error:', err);
      if (err.response?.status === 401) {
        setPasswordError('Incorrect password');
      } else if (err.response?.status === 410) {
        setExpired(true);
      } else {
        setPasswordError('An error occurred during verification');
      }
    } finally {
      setVerifying(false);
    }
  };

  const loadContent = async () => {
    try {
      setContentLoading(true);

      const response = await publicSharesApi.getContent(shareId, verifiedPassword);
      const blob = response.data;

      if (shareInfo.mimeType === 'application/pdf') {
        await loadPdf(blob);
      } else if (
        shareInfo.mimeType ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ) {
        await loadDocx(blob);
      } else if (
        shareInfo.mimeType ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        shareInfo.mimeType === 'application/vnd.ms-excel'
      ) {
        await loadExcel(blob);
      } else if (shareInfo.mimeType.startsWith('image/')) {
        const url = URL.createObjectURL(blob);
        setContent({ type: 'image', url });
      } else if (shareInfo.mimeType.startsWith('video/')) {
        const url = URL.createObjectURL(blob);
        setContent({ type: 'video', url });
      } else if (is3DModel(shareInfo.fileExtension)) {
        setContent({ type: 'model', blob, extension: shareInfo.fileExtension });
      } else {
        setContent({ type: 'unknown' });
      }
    } catch (err) {
      console.error('Load content error:', err);
      setError('Failed to load file content');
    } finally {
      setContentLoading(false);
    }
  };

  const loadPdf = async (blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setContent({ type: 'pdf' });
    } catch (err) {
      console.error('PDF load error:', err);
      setError('Failed to load PDF');
    }
  };

  const renderPage = async (pageNum) => {
    if (!pdfDoc || !canvasRef.current) return;

    try {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport
      }).promise;
    } catch (err) {
      console.error('Render page error:', err);
    }
  };

  const loadDocx = async (blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const result = await mammoth.convertToHtml({ arrayBuffer });
      setContent({ type: 'docx', html: result.value });
    } catch (err) {
      console.error('DOCX load error:', err);
      setError('Failed to load document');
    }
  };

  const loadExcel = async (blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheets = workbook.SheetNames.map((name) => {
        const worksheet = workbook.Sheets[name];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        return { name, data: jsonData };
      });

      setExcelData(sheets);
      setContent({ type: 'excel' });
    } catch (err) {
      console.error('Excel load error:', err);
      setError('Failed to load spreadsheet');
    }
  };

  const handleDownload = async () => {
    try {
      setDownloading(true);

      const response = await publicSharesApi.download(shareId, verifiedPassword);
      downloadBlob(response.data, shareInfo.fileName);
    } catch (err) {
      console.error('Download error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const getFileIcon = () => {
    if (!shareInfo) return File;
    const ext = shareInfo.fileExtension?.toLowerCase();
    if (ext === 'pdf') return FileText;
    if (['xlsx', 'xls'].includes(ext)) return Table;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image;
    if (['docx', 'doc'].includes(ext)) return FileText;
    if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext)) return Video;
    if (is3DModel(ext)) return Box;
    return File;
  };

  // Credits footer component
  const CreditsFooter = () => (
    <div className="text-center mt-6">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Shared via <span className="font-semibold">DocumentVault</span>
      </p>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
        Made by Varga Levente
      </p>
    </div>
  );

  // Not found page
  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
            <File className="w-12 h-12 text-gray-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Share not found
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            The share link you're looking for doesn't exist or has been deleted.
          </p>
          <a
            href="/"
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            Go to homepage
          </a>
          <CreditsFooter />
        </div>
      </div>
    );
  }

  // Expired page
  if (expired) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-12 h-12 text-orange-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Share link expired
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            This share link has expired and is no longer accessible.
          </p>
          <a
            href="/"
            className="text-primary-600 dark:text-primary-400 hover:underline"
          >
            Go to homepage
          </a>
          <CreditsFooter />
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-500 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-24 h-24 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-12 h-12 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            An error occurred
          </h1>
          <p className="text-gray-500 dark:text-gray-400">{error}</p>
          <CreditsFooter />
        </div>
      </div>
    );
  }

  // Password prompt
  if (needsPassword && !verified) {
    const IconComponent = getFileIcon();

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
            {/* File info */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
                <IconComponent className="w-7 h-7 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-gray-900 dark:text-white truncate">
                  {shareInfo?.fileName}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {getFileTypeLabel(shareInfo?.fileExtension)} - {formatFileSize(shareInfo?.fileSize)}
                </p>
              </div>
            </div>

            {/* Password form */}
            <div className="text-center mb-6">
              <Lock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Password protected file
              </h1>
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                Enter the password to view this file
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div className="relative mb-4">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="input pr-10"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {passwordError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg mb-4 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {passwordError}
                </div>
              )}

              <button
                type="submit"
                disabled={verifying || !password.trim()}
                className="btn btn-primary w-full"
              >
                {verifying ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Verifying...
                  </div>
                ) : (
                  'Open file'
                )}
              </button>
            </form>
          </div>

          <CreditsFooter />
        </div>
      </div>
    );
  }

  // Main content view
  const IconComponent = getFileIcon();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/30 rounded-xl flex items-center justify-center">
              <IconComponent className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="font-semibold text-gray-900 dark:text-white truncate max-w-[200px] sm:max-w-none">
                {shareInfo?.fileName}
              </h1>
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span>{getFileTypeLabel(shareInfo?.fileExtension)}</span>
                <span>{formatFileSize(shareInfo?.fileSize)}</span>
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                  <Lock className="w-3 h-3" />
                  AES-256
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* PDF controls */}
            {content?.type === 'pdf' && (
              <>
                <button
                  onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Zoom out"
                >
                  <ZoomOut className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[60px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <button
                  onClick={() => setScale((s) => Math.min(3, s + 0.25))}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Zoom in"
                >
                  <ZoomIn className="w-5 h-5" />
                </button>
                <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-2" />
              </>
            )}

            {shareInfo?.allowDownload && (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="btn btn-primary flex items-center gap-2"
              >
                {downloading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Download className="w-5 h-5" />
                )}
                <span className="hidden sm:inline">Download</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content with Sidebar */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Main content area */}
        <div className="flex-1 overflow-auto p-4 lg:p-8">
          {contentLoading && (
            <div className="flex items-center justify-center h-full">
              <div className="spinner"></div>
            </div>
          )}

        {/* PDF Viewer */}
          {content?.type === 'pdf' && (
            <div className="flex flex-col items-center">
              <canvas
                ref={canvasRef}
                className="shadow-lg max-w-full"
              />

              {/* Page navigation */}
              <div className="flex items-center gap-4 mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg shadow">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {currentPage} / {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}

          {/* DOCX Viewer */}
          {content?.type === 'docx' && (
            <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
              <div
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: content.html }}
              />
            </div>
          )}

          {/* Excel Viewer */}
          {content?.type === 'excel' && excelData && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
              {/* Sheet tabs */}
              {excelData.length > 1 && (
                <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                  {excelData.map((sheet, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveSheet(index)}
                      className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                        activeSheet === index
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 border-b-2 border-primary-600'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {sheet.name}
                    </button>
                  ))}
                </div>
              )}

              {/* Table */}
              <div className="overflow-auto max-h-[600px]">
                <table className="w-full border-collapse">
                  <tbody>
                    {excelData[activeSheet]?.data.map((row, rowIndex) => (
                      <tr key={rowIndex} className={rowIndex === 0 ? 'bg-gray-50 dark:bg-gray-700' : ''}>
                        {row.map((cell, cellIndex) => (
                          <td
                            key={cellIndex}
                            className={`border border-gray-200 dark:border-gray-600 px-3 py-2 text-sm ${
                              rowIndex === 0 ? 'font-semibold' : ''
                            }`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Image Viewer */}
          {content?.type === 'image' && (
            <div className="flex items-center justify-center">
              <img
                src={content.url}
                alt={shareInfo?.fileName}
                className="max-w-full max-h-[80vh] object-contain shadow-lg rounded-lg"
              />
            </div>
          )}

          {/* Video Viewer */}
          {content?.type === 'video' && (
            <div className="flex items-center justify-center">
              <video
                src={content.url}
                controls
                className="max-w-full max-h-[80vh] shadow-lg rounded-lg"
              >
                Your browser does not support video playback.
              </video>
            </div>
          )}

          {/* 3D Model Viewer */}
          {content?.type === 'model' && (
            <ModelViewer blob={content.blob} extension={content.extension} />
          )}

          {/* Unknown file type */}
          {content?.type === 'unknown' && (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <File className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                This file type cannot be previewed
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                {shareInfo?.allowDownload
                  ? 'You can download the file using the button above'
                  : 'Download is not allowed for this file'}
              </p>
            </div>
          )}
        </div>

        {/* Sidebar with file info */}
        <div className="w-full lg:w-80 bg-white dark:bg-gray-800 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 p-4 lg:p-6 overflow-y-auto">
          {shareInfo && (
            <>
              {/* Share expiration */}
              <div className="mb-6 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <div className="flex items-center gap-2 text-primary-700 dark:text-primary-400">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">{formatTimeRemaining(shareInfo.expiresAt)}</span>
                </div>
                <p className="text-xs text-primary-600 dark:text-primary-500 mt-1">
                  Expires: {formatDate(shareInfo.expiresAt)}
                </p>
              </div>

              {/* File info */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
                  File information
                </h3>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Type</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {getFileTypeLabel(shareInfo.fileExtension)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Size</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {formatFileSize(shareInfo.fileSize)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Shared on</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {formatDate(shareInfo.createdAt)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Shared by</dt>
                    <dd className="text-gray-900 dark:text-white truncate max-w-[150px]">
                      {shareInfo.uploaderName}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-gray-700 mt-2">
                    <dt className="text-gray-500 dark:text-gray-400">Encryption</dt>
                    <dd className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <Lock className="w-4 h-4" />
                      <span>AES-256</span>
                    </dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-gray-500 dark:text-gray-400">Download</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {shareInfo.allowDownload ? 'Allowed' : 'Disabled'}
                    </dd>
                  </div>
                  <div className="flex justify-between items-center">
                    <dt className="text-gray-500 dark:text-gray-400">Password</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {shareInfo.hasPassword ? 'Protected' : 'None'}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Credits */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                  Shared via <span className="font-semibold">DocumentVault</span>
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1">
                  Made by Varga Levente
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
          <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {shareInfo?.uploaderName}
            </span>
            <span className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              Encrypted storage
            </span>
          </div>
          <div className="text-gray-400 dark:text-gray-500">
            <span className="font-semibold">DocumentVault</span>
            <span className="mx-2">·</span>
            <span>Made by Varga Levente</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicSharePage;
